import { HttpParams } from '@angular/common/http';
import { Contact } from '../interfaces/contact.interface';

/**
 * Transformation d'un fichier CSV en JSON
 * @param csv Le CSV à transformer
 * @return Le JSON créé
 */
export function csvJSON(csv: string) {
  const lines: string[] = csv.split('\n');
  const headers: string[] = lines[0].replace('\r', '').split(';');
  const result: Array<Record<string, string>> = [];

  for (let i: number = 1; i < lines.length; i++) {
    const currentline: string = lines[i].replace('\r', '').trim();

    if (currentline === '') {
      continue;
    }

    const currentlineArray: string[] = currentline.split(';');
    const obj: Record<string, string> = {};

    for (let j: number = 0; j < headers.length; j++) {
      obj[headers[j]] = currentlineArray[j];
    }
    result.push(obj);
  }
  return JSON.stringify(result);
}

/**
 * Création des paramètres http
 * @param params Les paramètres
 */
export function createHttpParams(params: { [key: string]: string | string[] }): HttpParams {
  let httpParams = new HttpParams();

  Object.keys(params).forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(params, key)) {
      const value = params[key];
      if (Array.isArray(value)) {
        value.forEach((item) => {
          httpParams = httpParams.append(key, item.toString());
        });
      } else {
        httpParams = httpParams.set(key, value.toString());
      }
    }
  });

  return httpParams;
}

/**
 * Méthode permettant de ne garder que le contenu entre parenthèse si parenthèse présente
 * sinon ça renvoit le texte de base
 * @param value La valeur a tester
 */
export function extractParentheses(value: string): string {
  const match = value && value.match(/\(([^)]+)\)/);
  return match ? match[1] : value;
}

/**
 * Séparer les fichiers en groupe de taille < 1Mo
 * @param files Les fichiers à grouper
 */
const MAX_CHUNK_SIZE = 1024 * 1024;
export function chunkFiles(files: string[]): string[][] {
  const chunks: string[][] = [];
  let currentChunk: string[] = [];
  let totalSize = 0;

  for (const file of files) {
    const fileSize = file.length;
    totalSize += fileSize;
    currentChunk.push(file);

    if (totalSize > MAX_CHUNK_SIZE) {
      chunks.push(currentChunk);
      currentChunk = [];
      totalSize = 0;
    }
  }

  if (currentChunk.length > 0) {
    chunks.push(currentChunk);
  }

  return chunks;
}

/**
 * Formater les emails pour affichage dans le tooltip
 * @param contacts la liste des contacts ou la liste des emails tombé en erreur d'envoi
 */
export function formatEmails(contacts: Contact[] | string[]): string {
  return contacts
    .map((contact) => {
      // si c'est un objet Contact avec un champ email, on prend contact.email
      if (typeof contact === 'object') {
        return contact.email;
      }
      // sinon c'est déjà un string
      return contact;
    })
    .join('<br>');
}

// Methode pour transformer un Json en csv
export function toCsv<T>(data: T[], headers: { key: keyof T; label: string }[]): string {
  const headerLine = headers.map((h) => `"${h.label}"`).join(';');

  const lines = data.map((row) =>
    headers
      .map((h) => {
        const value = row[h.key];
        return `"${String(value ?? '').replace(/"/g, '""')}"`;
      })
      .join(';'),
  );

  return [headerLine, ...lines].join('\n');
}

// Methode pour telecharger un csv
export function downloadCsv(filename: string, csv: string) {
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csv], {
    type: 'text/csv;charset=utf-8;',
  });

  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();

  URL.revokeObjectURL(url);
}
